import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const NetJrfSidebar = () => (
  <aside className="w-full lg:w-80 xl:w-96 flex-shrink-0">
    <div className="lg:sticky lg:top-28 space-y-6">
      <div className="glass-card">
        <h3 className="text-lg font-bold text-text-light mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-accent-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quick Links
        </h3>
        <div className="space-y-2">
          {[
            { label: '📝 Paper 1 Mock Test (Free)', path: '/ugc-net-mock' },
            { label: '📚 Study Materials', path: '/study-materials' },
            { label: '🌍 Current Affairs Test', path: '/current-affairs-test' },
            { label: '🧠 Reasoning Practice', path: '/reasoning-test' },
            { label: '🎥 Self-Paced Course', path: '/self-paced' },
            { label: '🎓 Expert Counselling', path: '/cuet-counselling' },
          ].map(l => (
            <Link key={l.path} to={l.path}
              className="flex items-center gap-3 p-3 rounded-xl bg-bg-secondary hover:bg-primary/10 transition-colors group">
              <svg className="w-4 h-4 text-text-muted group-hover:text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-text-light group-hover:text-primary transition-colors text-sm">{l.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="glass-card bg-gradient-to-br from-green-900/30 to-primary/20 border-green-500/20">
        <h3 className="text-lg font-bold text-text-light mb-2">Free Paper 1 Mock Test</h3>
        <p className="text-text-muted text-sm mb-4">Real NTA pattern, instant score, and detailed analysis for all 9 Paper 1 units.</p>
        <Link to="/ugc-net-mock" className="btn-primary block text-center py-3">Start Free Test →</Link>
      </div>

      <div className="glass-card">
        <h3 className="text-lg font-bold text-text-light mb-4">📰 Related Articles</h3>
        <div className="space-y-4">
          {[
            { title: 'UGC NET Paper 1: Complete Syllabus & Weightage', cat: 'Syllabus', path: '/ugc-net-mock' },
            { title: 'NET vs JRF: What is the Difference?', cat: 'Exam Info', path: '/blogs' },
            { title: 'Teaching Aptitude: Most Asked Questions', cat: 'Paper 1', path: '/ugc-net-mock' },
            { title: 'How to Clear UGC NET in 3 Months', cat: 'Strategy', path: '/blogs' },
            { title: 'Best Books for UGC NET Paper 1', cat: 'Resources', path: '/study-materials' },
          ].map((a, i) => (
            <Link key={i} to={a.path} className="block group">
              <span className="text-xs text-primary">{a.cat}</span>
              <h4 className="text-text-light group-hover:text-primary transition-colors line-clamp-2 mt-0.5 text-sm">{a.title}</h4>
              {i < 4 && <div className="border-b border-glass-border mt-3" />}
            </Link>
          ))}
        </div>
      </div>
    </div>
  </aside>
);

const NetJrfPage = () => (
  <>
    <Helmet>
      <title>UGC NET / JRF 2026 – Complete Preparation Guide | BESST</title>
      <meta name="description" content="Crack UGC NET / JRF 2026 with BESST — free Paper 1 mock tests, study materials, reasoning practice, and expert-curated resources. Start preparing today." />
      <link rel="canonical" href="https://besst.in/net-jrf" />
    </Helmet>

    <div className="min-h-screen">
      <div className="bg-gradient-to-r from-green-900/30 via-bg-secondary to-primary/20 pt-32 pb-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="hero-badge mb-4">🏆 JRF / Assistant Professor</div>
          <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-4 leading-tight">UGC NET / JRF 2026</h1>
          <p className="text-xl text-text-muted max-w-2xl mb-8">
            Qualify for Junior Research Fellowship or Assistant Professorship. Master both Paper 1 and Paper 2 with structured preparation and expert resources.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/ugc-net-mock" className="btn-primary">📝 Free Paper 1 Mock Test</Link>
            <Link to="/study-materials" className="btn-secondary">Study Materials</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          <article className="flex-1 min-w-0 space-y-10">

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">1</span>
                What is UGC NET / JRF?
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                The University Grants Commission National Eligibility Test (UGC NET) is conducted by NTA to determine eligibility for the post of Assistant Professor and/or Junior Research Fellowship (JRF) in Indian universities and colleges. Clearing NET makes you eligible to teach; clearing with JRF rank also entitles you to a monthly fellowship for research.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <h4 className="text-blue-400 font-semibold mb-2">📋 NET Qualification</h4>
                  <p className="text-text-muted text-sm">Eligibility for Assistant Professor in universities and colleges across India.</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <h4 className="text-green-400 font-semibold mb-2">🏆 JRF (Junior Research Fellowship)</h4>
                  <p className="text-text-muted text-sm">Monthly fellowship of ₹37,000+ for 2 years (extendable) to pursue PhD research.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">2</span>
                Exam Structure
              </h2>
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-6 mb-4">
                <h4 className="text-primary font-semibold mb-3">📌 Key Facts — UGC NET 2026</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-text-muted text-sm">
                  {[
                    ['Frequency', 'Twice a year (June & December)'],
                    ['Mode', 'Computer-Based Test (CBT)'],
                    ['Total Duration', '3 hours (combined)'],
                    ['Papers', 'Paper 1 (General) + Paper 2 (Subject)'],
                    ['Subjects', '83 subjects available'],
                    ['Marking', '+2 per correct, no negative marking'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="font-medium text-text-light">{k}:</span><span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-bg-secondary">
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Paper</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Questions</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Max Marks</th>
                      <th className="border border-glass-border px-4 py-3 text-left text-text-light">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-text-muted">
                    <tr>
                      <td className="border border-glass-border px-4 py-3"><span className="font-medium text-text-light">Paper 1</span> — Teaching & Research Aptitude</td>
                      <td className="border border-glass-border px-4 py-3">50</td>
                      <td className="border border-glass-border px-4 py-3">100</td>
                      <td className="border border-glass-border px-4 py-3">1 hr</td>
                    </tr>
                    <tr>
                      <td className="border border-glass-border px-4 py-3"><span className="font-medium text-text-light">Paper 2</span> — Subject Specific</td>
                      <td className="border border-glass-border px-4 py-3">100</td>
                      <td className="border border-glass-border px-4 py-3">200</td>
                      <td className="border border-glass-border px-4 py-3">2 hrs</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">3</span>
                Paper 1 Syllabus Coverage
              </h2>
              <p className="text-text-muted mb-4">Paper 1 is common for all candidates. It tests teaching and research aptitude across 9 units:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {['Teaching Aptitude', 'Research Aptitude', 'Comprehension', 'Communication', 'Logical Reasoning', 'Data Interpretation', 'ICT', 'People & Environment', 'Higher Education'].map((u, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 bg-bg-secondary rounded-xl text-sm">
                    <span className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <span className="text-text-light">{u}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Link to="/ugc-net-mock" className="glass-card block hover:border-primary/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-text-light font-bold">Free Paper 1 Mock Test</h3>
                      <p className="text-text-muted text-sm mt-1">All 9 units covered — real NTA pattern with instant score & analysis</p>
                    </div>
                    <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-text-light mb-4 flex items-center gap-3">
                <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm font-bold">4</span>
                Preparation Strategy
              </h2>
              <div className="space-y-4">
                {[
                  { t: 'Start with Paper 1', d: 'Paper 1 is entirely scoreable — it has a fixed syllabus and predictable question types. Secure maximum marks here before deep-diving into Paper 2.' },
                  { t: 'Master Current Affairs', d: 'Current affairs, environment, and ICT questions in Paper 1 change each cycle. Regular reading and daily GK practice is essential.' },
                  { t: 'Subject Paper 2 Strategy', d: 'Cover the complete UGC-specified syllabus for your subject. Focus on PYQs — NET reuses question concepts frequently across sessions.' },
                  { t: 'Mock Test Cadence', d: 'Take a full-length combined mock (Paper 1 + Paper 2) at least once a week in the final 6 weeks. Time management is critical in a 3-hour exam.' },
                ].map((s, i) => (
                  <div key={i} className="glass-card flex gap-4 items-start">
                    <div className="w-8 h-8 flex-shrink-0 bg-primary/20 rounded-full flex items-center justify-center font-bold text-primary text-sm">{i + 1}</div>
                    <div>
                      <h3 className="text-text-light font-bold mb-1">{s.t}</h3>
                      <p className="text-text-muted text-sm">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-gradient-to-r from-primary/20 to-accent-gold/20 border border-primary/30 rounded-xl p-6">
              <h3 className="text-xl font-bold text-text-light mb-2">Prepare for UGC NET / JRF with BESST</h3>
              <p className="text-text-muted mb-4">Free Paper 1 mock tests, study materials, current affairs practice, and reasoning tests — everything in one place.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/ugc-net-mock" className="btn-primary">Start Free Mock Test →</Link>
                <Link to="/study-materials" className="btn-secondary">Study Materials</Link>
              </div>
            </div>
          </article>

          <NetJrfSidebar />
        </div>
      </div>
    </div>
  </>
);

export default NetJrfPage;
